import { create } from 'zustand';
import useVoyagerStore from '../stores/useVoyagerStore';

/**
 * Close → History with an Undo (#765, owner decision at the round-3
 * checkpoint): the close is sent at once — nothing is held pending, there is
 * no timer. The Undo becomes actionable only once the server has acknowledged
 * the close, so a reopen can never race an unacknowledged close; it then sends
 * /reopen. A close the server refuses is rolled back by the threads slice and
 * offers no Undo; a reopen the server refuses leaves the thread closed and says
 * so. (Delete has no Undo: it is confirmed, then sent.)
 */
export type CloseToast = {
  threadId: string;
  title: string;
  /** closing: sent, not acknowledged (Undo disabled) · closed: acknowledged (Undo enabled) · reopening: Undo sent ·
   *  close-failed: rolled back, still open · reopen-failed: still closed, in History */
  phase: 'closing' | 'closed' | 'reopening' | 'close-failed' | 'reopen-failed';
  /** the threads generation (workspace) it belongs to: a toast from another workspace is never shown or acted on */
  generation: number;
  seq: number;
};

export const useCloseToast = create<{ toast: CloseToast | null }>(() => ({ toast: null }));
let seq = 0;

/** Only the toast it was made for may change: a newer close replaces it, and a late reply to the old one is ignored. */
const update = (mine: number, phase: CloseToast['phase'] | null) => {
  const t = useCloseToast.getState().toast;
  if (!t || t.seq !== mine) return;
  useCloseToast.setState({ toast: phase ? { ...t, phase } : null });
};

export async function closeWithUndo(threadId: string, title: string): Promise<void> {
  const generation = useVoyagerStore.getState().threadsGeneration;
  const mine = ++seq;
  useCloseToast.setState({ toast: { threadId, title, phase: 'closing', generation, seq: mine } });
  const ok = await useVoyagerStore.getState().closeThread(threadId);
  if (useVoyagerStore.getState().threadsGeneration !== generation) { update(mine, null); return; }
  update(mine, ok ? 'closed' : 'close-failed');
}

export async function undoClose(): Promise<void> {
  const t = useCloseToast.getState().toast;
  if (!t || t.phase !== 'closed' || t.generation !== useVoyagerStore.getState().threadsGeneration) return;
  update(t.seq, 'reopening');
  const ok = await useVoyagerStore.getState().reopenThread(t.threadId);
  if (useVoyagerStore.getState().threadsGeneration !== t.generation) { update(t.seq, null); return; }
  update(t.seq, ok ? null : 'reopen-failed');
}

export function dismissCloseToast(): void {
  useCloseToast.setState({ toast: null });
}
